import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Globe, ChevronDown } from 'lucide-react';
import ForgotPasswordModal from './ForgotPasswordModal';
import { useSettings } from '../contexts/SettingsContext';

function Login() {
  const { t, i18n } = useTranslation();
  const { getBranding } = useSettings();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'العربية', flag: '🇩🇿' }
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setShowLanguageMenu(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="h-screen min-h-screen h-[100dvh] overflow-y-auto bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 py-4 sm:py-6 px-4 sm:px-6 lg:px-8" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Language Switcher */}
      <div className="absolute top-4 right-4">
        <div className="relative">
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-neutral-200"
          >
            <Globe className="h-4 w-4 text-neutral-600" />
            <span className="text-sm font-medium text-neutral-700">
              {languages.find(lang => lang.code === i18n.language)?.flag} {languages.find(lang => lang.code === i18n.language)?.name}
            </span>
            <ChevronDown className="h-4 w-4 text-neutral-500" />
          </button>

          {showLanguageMenu && (
            <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-neutral-200 z-50">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${i18n.language === lang.code ? 'bg-blue-50 text-blue-700' : 'text-neutral-700'
                    }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="text-sm font-medium">{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex min-h-full items-center justify-center">
      <div className="max-w-md w-full space-y-4 sm:space-y-6 my-auto animate-[fadeInUp_0.6s_ease-out]"> 
        <div>
          <div className="mx-auto h-20 w-20 sm:h-24 sm:w-24 flex items-center justify-center rounded-full bg-white shadow-2xl border-4 border-white transform hover:scale-105 transition-transform duration-300">
            {!logoError && getBranding('club.logo.url') ? (
              <img
                src={getBranding('club.logo.url', '/logo.jpg')}
                alt={getBranding('club.name', t('app.logoAlt'))}
                className="h-16 w-16 sm:h-20 sm:w-20 object-contain rounded-full"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-secondary-100">
                <span className="text-2xl sm:text-3xl">🐎</span>
              </div>
            )}
          </div>
          <h1 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {getBranding('club.name') || t('app.clubName')}
          </h1>
          <h2 className="mt-1 sm:mt-2 text-center text-base sm:text-lg font-medium text-primary-600">
            {getBranding('club.subtitle') || t('app.clubLocation')}
          </h2>
          <p className="mt-2 sm:mt-3 text-center text-sm text-gray-500 max-w-sm mx-auto">
            {t('auth.welcome')}
          </p>
          
          <div className="mt-4 sm:mt-6 bg-blue-50/80 backdrop-blur-sm border border-blue-100 rounded-2xl p-3 sm:p-4 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="p-1 bg-blue-100 rounded-full">
                  <span className="text-blue-600 text-sm font-bold">i</span>
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-blue-900">
                  Personnel Portal
                </h3>
                <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                  Restricted access for authorized personnel only. 
                  <br />
                  <span className="opacity-75">Riders & Owners: Please use the mobile app.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-5 sm:p-6 lg:p-8 border border-white/20 ring-1 ring-gray-900/5">
          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r-lg flex items-center shadow-sm animate-[shake_0.5s_ease-in-out]">
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            <div className="space-y-5">
              <div className="group">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  {t('auth.email')}
                </label>
                <div className="relative transition-all duration-300 transform group-hover:-translate-y-0.5">
                  <Mail className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors ${i18n.language === 'ar' ? 'right-4' : 'left-4'}`} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`w-full py-3 sm:py-3.5 border-2 border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all duration-300 text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:bg-white ${i18n.language === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="group">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  {t('auth.password')}
                </label>
                <div className="relative transition-all duration-300 transform group-hover:-translate-y-0.5">
                  <Lock className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors ${i18n.language === 'ar' ? 'right-4' : 'left-4'}`} />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className={`w-full py-3 sm:py-3.5 border-2 border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all duration-300 text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:bg-white ${i18n.language === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3.5 sm:py-4 px-6 border border-transparent rounded-2xl shadow-lg shadow-primary-500/30 text-base font-bold text-white bg-gradient-to-r from-primary-600 to-emerald-600 hover:from-primary-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t('auth.signingIn')}
                  </>
                ) : (
                  t('auth.signIn')
                )}
              </button>
            </div>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-gray-500 hover:text-primary-600 font-medium transition-colors hover:underline"
              >
                {t('auth.forgotPassword')}
              </button>
            </div>
          </form>
        </div>

        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
        />
      </div>
      </div>
    </div>
  );
}

export default Login;
